import React, { useState, useEffect } from 'react';
import { Linkedin, Mail, Building, Users, Award, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

const Team = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await fetch('https://apigulmohar.dodunsoftsolutions.com/api/team/company/DevBhoomiGroup');
        
        if (!response.ok) {
          throw new Error('Failed to fetch team members');
        }
        
        const data = await response.json();
        
        // Add founder at the beginning if not present
        const founderExists = data.some(member => member.name === "Rajinder Vashista");
        
        let updatedData = [...data];
        if (!founderExists) {
          updatedData.unshift({
            _id: "founder-id",
            name: "Rajinder Vashista",
            title: "Founder & Chairman",
            description: "Founded the Dev Bhoomi Group in 2005. Under his visionary leadership, the group has expanded to seven companies across four verticals.",
            imageUrl: "", // Placeholder, replace with actual image if available
            order: -1, // Ensure founder appears first
            email: "",
            linkedIn: ""
          });
        }
        
        // Sort team members by order if available
        const sortedData = updatedData.sort((a, b) => {
          // If order is null, place at the end
          if (a.order === null) return 1;
          if (b.order === null) return -1;
          return a.order - b.order;
        });
        
        setTeamMembers(sortedData);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchTeamMembers();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-navy-950">
        <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center pt-34 min-h-screen bg-navy-950 text-red-400">
        <div className="text-center p-8 rounded-lg border border-red-400 bg-red-900 bg-opacity-10 backdrop-blur-sm">
          <h2 className="text-2xl font-bold mb-4">Error Loading Team</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 pt-36 text-white">
      {/* Hero Section */}
      <div className="relative bg-navy-900 py-24">
        <div className="absolute inset-0 bg-[url('/api/placeholder/1920/400')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/80 to-navy-950/95"></div>
        <div className="relative container mx-auto px-4 text-center">
          <h1 className="text-6xl font-bold mb-6 tracking-tight">
            Our <span className="text-gold">Leadership</span> Team
          </h1>
          <div className="flex items-center justify-center mb-10">
            <div className="h-px w-16 bg-navy-700"></div>
            <div className="w-24 h-1 bg-gold mx-3"></div>
            <div className="h-px w-16 bg-navy-700"></div>
          </div>
          <p className="text-xl max-w-2xl mx-auto text-gray-300 font-light">
            Meet the visionaries behind Dev Bhoomi Group, founded by Mr. Rajinder Vashista in 2005.
          </p>
        </div>
      </div>
      
      {/* Team Grid */}
      <div className="container mx-auto px-4 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-12">
          {teamMembers.map((member) => (
            <div 
              key={member._id} 
              className="group relative overflow-hidden rounded-lg bg-navy-900/20 border border-navy-800 hover:border-gold/60 transition-all duration-500 shadow-lg hover:shadow-gold/10"
            >
              <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Image with overlay */}
              <div className="aspect-[4/5] overflow-hidden">
                {member.imageUrl ? (
                  <img 
                    src={member.imageUrl} 
                    alt={member.name} 
                    className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <div className="w-full h-full bg-navy-800 flex items-center justify-center bg-opacity-60 backdrop-blur-sm">
                    <div className="relative">
                      <div className="absolute -inset-1 rounded-full bg-gold/20 blur-sm"></div>
                      <span className="relative text-6xl text-gold font-bold">
                        {member.name.charAt(0)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Information Card */}
              <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-navy-950 via-navy-950/95 to-navy-950/0">
                <h3 className="text-2xl font-bold text-white mb-2">{member.name}</h3>
                <div className="flex items-center mb-4">
                  <div className="h-px w-12 bg-gold mr-3"></div>
                  <p className="text-gold font-medium">{member.title}</p>
                </div>
                
                <p className="text-gray-300 mb-6 line-clamp-2 group-hover:line-clamp-none transition-all">
                  {member.description}
                </p>
                
                <div className="flex gap-4">
                  {member.linkedIn && (
                    <a 
                      href={member.linkedIn} 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-navy-800 hover:bg-gold text-white hover:text-navy-950 transition-colors duration-300 shadow-md"
                    >
                      <Linkedin size={20} />
                    </a>
                  )}
                  
                  {member.email && (
                    <a 
                      href={`mailto:${member.email}`}
                      className="p-2 rounded-full bg-navy-800 hover:bg-gold text-white hover:text-navy-950 transition-colors duration-300 shadow-md"
                    >
                      <Mail size={20} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Call to Action */}
      <div className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-navy-900 bg-opacity-80"></div>
        <div className="absolute inset-0 bg-[url('/api/placeholder/1920/400')] bg-cover bg-center opacity-10"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent"></div>
        <div className="container relative mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Join the <span className="text-gold">Dev Bhoomi</span> Family
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-10 font-light">
            Be part of Himachal Pradesh's leading business group with a diverse portfolio across multiple verticals. We're always looking for talented individuals to join our journey.
          </p>
          <Link to="/careers">
            <button className="px-10 py-3 bg-transparent hover:bg-gold text-gold hover:text-navy-950 font-semibold rounded-md transition-all duration-300 border border-gold hover:border-transparent shadow-lg hover:shadow-gold/20">
              View Open Positions
            </button>
          </Link>
        </div>
      </div>
      
      {/* Add custom styles */}
      <style jsx global>{`
        /* Custom classes */
        .bg-navy-900 {
          background-color: #0f172a;
        }
        .bg-navy-950 {
          background-color: #030712;
        }
        .bg-navy-800 {
          background-color: #1e293b;
        }
        .bg-gold {
          background-color: #d4af37;
        }
        .border-gold {
          border-color: #d4af37;
        }
        .text-gold {
          color: #d4af37;
        }
      `}</style>
    </div>
  );
};

export default Team;